/**
 * ListPickerModal – reusable bottom sheet for selecting one item from a list.
 * Used by AreaPickerModal and can be used for slots, generic dropdowns, etc.
 * Uses BottomSheetPopup + appTheme.modal / appTheme.picker.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { View, Text, StyleSheet, FlatList, Platform, Keyboard } from 'react-native';
import { SvgIcon } from './SvgIcon';
import { Clickable } from './Clickable';
import { Input } from './Input';
import { BottomSheetPopup } from './BottomSheetPopup';
import { appTheme, useThemeColors, type ThemeColors } from '../../shared/theme';
import { useTranslation } from '../../shared/i18n';
import { useAuthSheetKeyboardInset } from '../../shared/auth';

export interface ListPickerItem {
  id: number | string;
  label: string;
  [key: string]: any;
}

export interface ListPickerModalProps {
  visible: boolean;
  onClose: () => void;
  onSelect: (item: ListPickerItem) => void;
  title: string;
  items: ListPickerItem[];
  selectedId?: number | string | null;
  searchable?: boolean;
  searchPlaceholder?: string;
  emptyTitle?: string;
  emptyText?: string;
  renderItemLabel?: (item: ListPickerItem) => string;
  keyExtractor?: (item: ListPickerItem) => string;
  /** Optional icon name for each row (e.g. 'map' for area picker). Omit for no icon. */
  itemIcon?: string;
}

function createListPickerStyles(c: ThemeColors) {
  return StyleSheet.create({
    searchWrap: {
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: c.border,
      paddingHorizontal: appTheme.modal.headerPadding,
      paddingTop: appTheme.modal.headerPadding,
      paddingBottom: appTheme.modal.footerPaddingTop,
      marginBottom: appTheme.spacing.sm,
    },
    clearWrap: {},
    clearBtn: { padding: appTheme.spacing.xs },
    listContent: {
      padding: appTheme.spacing.md,
    },
    itemWrap: {
      marginBottom: appTheme.spacing.sm,
    },
    itemInner: {
      flexDirection: 'row',
      alignItems: 'center',
      padding: appTheme.spacing.md,
      borderRadius: appTheme.picker.borderRadius,
      borderWidth: appTheme.picker.borderWidth,
      gap: appTheme.spacing.md,
    },
    itemSelected: {
      borderColor: c.primary,
      backgroundColor: `${c.primary}10`,
    },
    itemText: {
      flex: 1,
      ...appTheme.typography.bodySmall,
    },
    itemTextSelected: {
      color: c.primary,
      fontWeight: '600',
    },
    empty: {
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: appTheme.spacing.xl * 2,
    },
    emptyTitle: {
      ...appTheme.typography.h3,
      marginTop: appTheme.spacing.md,
    },
    emptyText: {
      ...appTheme.typography.body,
      marginTop: appTheme.spacing.sm,
    },
  });
}

export const ListPickerModal: React.FC<ListPickerModalProps> = ({
  visible,
  onClose,
  onSelect,
  title,
  items,
  selectedId,
  searchable = false,
  searchPlaceholder,
  emptyTitle = 'No items',
  emptyText = 'No items available',
  renderItemLabel = item => item.label,
  keyExtractor = item => String(item.id),
  itemIcon,
}) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredItems, setFilteredItems] = useState(items);
  const colors = useThemeColors() ?? appTheme.colors;
  const styles = useMemo(() => createListPickerStyles(colors), [colors]);
  const keyboard_inset = useAuthSheetKeyboardInset();
  const effectiveSearchPlaceholder = searchPlaceholder ?? t('common.search');
  const modal = appTheme.modal;
  const picker = appTheme.picker;

  useEffect(() => {
    setFilteredItems(items);
  }, [items]);

  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredItems(items);
      return;
    }
    const q = searchQuery.toLowerCase();
    setFilteredItems(items.filter(item => renderItemLabel(item).toLowerCase().includes(q)));
  }, [searchQuery, items, renderItemLabel]);

  const handleSelect = (item: ListPickerItem) => {
    Keyboard.dismiss();
    try {
      onSelect(item);
    } finally {
      setSearchQuery('');
      setFilteredItems(items);
      onClose();
    }
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setFilteredItems(items);
  };

  const renderItem = ({ item }: { item: ListPickerItem }) => {
    const isSelected = item.id === selectedId || String(item.id) === String(selectedId);
    return (
      <Clickable
        key={keyExtractor(item)}
        style={styles.itemWrap}
        onPress={() => handleSelect(item)}
        accessibilityLabel={renderItemLabel(item)}
        accessibilityRole="button">
        <View style={[styles.itemInner, isSelected && styles.itemSelected, { borderColor: colors.border, backgroundColor: colors.surface }]}>
          {itemIcon ? <SvgIcon name={itemIcon as any} size={20} color={isSelected ? colors.primary : colors.textSecondary} /> : null}
          <Text style={[styles.itemText, { color: colors.text }, isSelected && styles.itemTextSelected]} numberOfLines={1}>
            {renderItemLabel(item)}
          </Text>
          {isSelected ? <SvgIcon name="check" size={20} color={colors.primary} /> : null}
        </View>
      </Clickable>
    );
  };

  return (
    <BottomSheetPopup visible={visible} onClose={onClose} title={title} animationType="slide" keyboardAvoiding={searchable}>
      {searchable && (
        <View style={[styles.searchWrap, { borderBottomColor: colors.border }]}>
          <Input
            placeholder={effectiveSearchPlaceholder}
            value={searchQuery}
            onChangeText={setSearchQuery}
            leftAccessory={<SvgIcon name="search" size={20} color={colors.textSecondary} />}
            rightAccessory={
              searchQuery.length > 0 ? (
                <Clickable onPress={handleClearSearch} accessibilityLabel={t('common.clear')} accessibilityRole="button">
                  <View style={styles.clearWrap}>
                    <View style={styles.clearBtn}>
                    <SvgIcon name="x" size={20} color={colors.textSecondary} />
                    </View>
                  </View>
                </Clickable>
              ) : null
            }
            containerStyle={{ marginBottom: 0 }}
          />
        </View>
      )}

      {filteredItems.length > 0 ? (
        <FlatList
          style={{ flex: 1, minHeight: 0 }}
          data={filteredItems}
          renderItem={renderItem}
          keyExtractor={keyExtractor}
          keyboardShouldPersistTaps="always"
          keyboardDismissMode="on-drag"
          nestedScrollEnabled={Platform.OS === 'android'}
          automaticallyAdjustKeyboardInsets={Platform.OS === 'ios'}
          contentContainerStyle={[
            styles.listContent,
            searchable && Platform.OS === 'android' ? { paddingBottom: keyboard_inset } : null,
          ]}
        />
      ) : (
        <View style={[styles.empty, { flex: 1, minHeight: 0 }]}>
          <SvgIcon name="map" size={48} color={colors.textLight} />
          <Text style={[styles.emptyTitle, { color: colors.text }]}>{emptyTitle}</Text>
          <Text style={[styles.emptyText, { color: colors.textSecondary }]}>{emptyText}</Text>
        </View>
      )}
    </BottomSheetPopup>
  );
};

export default ListPickerModal;
